import { useState } from "react";

import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { resolveAskingPrice } from "@/common/utils/asking-price.utils";
import { cn } from "@/common/utils/css.utils";
import { formatPrice } from "@/common/utils/intl.utils";
import { resolveCategoryTitle } from "@/common/utils/sold-alternatives.utils";
import {
  UNAVAILABLE_VARIANT,
  UnavailableVariant,
} from "@/common/utils/sold-product.utils";
import ProductImagesLightBox from "@/components/ProductDetail/ProductImagesLightBox";
import Image from "@/components/common/Image";
import { defaultLocale } from "@/i18n/config";
import { Heading } from "@/ui/cva/Heading";
import { InfoLabel } from "@/ui/cva/InfoLabel";
import { Body } from "@/ui/typography/Body";

/**
 * The left column of the sold page's hero: what the item was, and that it
 * is gone.
 *
 * Replaces `SoldItemStrip` after the design review. The strip said the
 * same things, but it said them in a row of small text with a bespoke pink
 * badge, and the status had to be carried by a pill rather than by the
 * page's own heading. The review's target structure puts the status first,
 * as the h1, and gives the item enough context to be recognised without
 * reviving its sales pitch.
 *
 * Four blocks, in the order the review specifies:
 *
 *  1. **"This item has been sold" as the h1.** The status is the first
 *     thing read. The item's own title follows immediately as an h2, so a
 *     visitor arriving from a search result or an assistant citation can
 *     still confirm they landed on the thing they clicked. The page is
 *     `noindex,follow`, so the heading is not carrying any ranking weight
 *     that moving it costs us.
 *  2. **Photo strip.** Up to four 64px thumbnails from the original
 *     listing: visual confirmation of which item this was, without the
 *     full gallery that used to occupy the whole first screen. Clicking
 *     one opens the PDP's own lightbox on the complete set, which is what
 *     makes this page usable for someone who has to look at the object
 *     rather than shop for one; see the note above the strip.
 *  3. **Status label.** The `InfoLabel` component (the design system's
 *     Label), Brand colour, Light variant, rounded-full corners. The strip
 *     used a hand-rolled `bg-attention-500` pill, which was Attention
 *     rather than Brand and off the catalogued order/ad-state mapping.
 *  4. **Identity and spec pills.** Title, category, condition and the
 *     price it was listed at, then a handful of facts about the object
 *     itself as square Light labels. Square is the documented corner for
 *     "a fact about the item", against rounded-full for a status.
 *
 * Of the three WHO-5240 variants, two now read the same. A reserved
 * listing says "sold" alongside a completed one, because in production
 * RESERVED is where a sold item comes to rest rather than a 48-hour
 * waiting room; see the note on `claimsSold` below. A withdrawn listing
 * still claims no sale at all, because nothing was ever bought.
 *
 * The realised sale price is still absent here and everywhere else on the
 * page: it is per-item transaction data, redacted out of the props and the
 * Apollo cache by `redactSoldPrice` in `pages/products/[slug].js`. The
 * asking price was public while the listing was live, so it is shown in
 * the past tense.
 */

/** Enough to recognise the piece, not enough to be a gallery. */
export const SUMMARY_THUMBNAIL_COUNT = 4;

/**
 * Width we ask the image CDN for, for a 64px box.
 *
 * The gateway hands us `...jpeg?width=1280`, and `components/common/Image`
 * marks product images `unoptimized`, so whatever width is in that URL is
 * what the browser downloads. Four thumbnails were therefore pulling four
 * 1280px JPEGs, about a megabyte, to fill four 64px squares. 128 is 2x for
 * a retina screen and lands in single-digit kilobytes.
 */
const THUMBNAIL_WIDTH = 128;

/**
 * Re-ask the CDN for a thumbnail-sized rendition.
 *
 * Only the `width` parameter is touched, and only when the URL parses: an
 * unexpected shape falls through to the original rather than risking a
 * broken `src`, which on this page would mean no photo at all.
 */
export const toThumbnailUrl = (url: string) => {
  try {
    const parsed = new URL(url);

    parsed.searchParams.set("width", String(THUMBNAIL_WIDTH));

    return parsed.toString();
  } catch {
    return url;
  }
};

type SoldItemSummaryProps = {
  product: ProductBySlugNonNullable;
  /**
   * Which claim the hero is allowed to make (WHO-5240). Only `sold` may
   * state that the item was bought; see `resolveProductAvailability`.
   */
  variant?: UnavailableVariant;
  className?: string;
};

/**
 * Structural shapes for the fields read below.
 *
 * Deliberately not the generated types: `src/Apollo/schema.ts` is
 * gitignored codegen, so a callback whose parameter type comes from it is
 * an implicit `any` for anyone without a gateway token.
 */
type AttributeLike = {
  __typename?: string | null;
  title?: string | null;
} | null;
type ImageLike = { url?: string | null } | null;

const attributesOfType = (
  product: ProductBySlugNonNullable,
  typename: string
) =>
  ((product.attributes ?? []) as AttributeLike[])
    .filter((attribute: AttributeLike) => attribute?.__typename === typename)
    .map((attribute: AttributeLike) => attribute?.title)
    .filter((title): title is string => !!title);

const brandTitle = (product: ProductBySlugNonNullable) =>
  attributesOfType(product, "Brand")[0] ?? null;

const imageUrls = (product: ProductBySlugNonNullable) =>
  ((product.images ?? []) as ImageLike[])
    .map((image: ImageLike) => image?.url)
    .filter((url): url is string => !!url)
    .slice(0, SUMMARY_THUMBNAIL_COUNT);

export const SoldItemSummary = ({
  product,
  variant = UNAVAILABLE_VARIANT.Sold,
  className,
}: SoldItemSummaryProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();

  const resolvedLocale = locale ?? defaultLocale;

  const thumbnails = imageUrls(product);

  /*
    Which photo the lightbox is showing, or null when it is closed.

    Kept as an index rather than a boolean plus a second piece of state,
    so the two can never disagree about which image is open.
  */
  const [openImageIndex, setOpenImageIndex] = useState<number | null>(null);

  /*
    The status, said twice: once as the page's own heading and once as the
    label beside the title.

    ## Why `pending` reads as sold

    `RESERVED` was modelled as a live 48-hour reservation that could still
    fall through, so the copy said "A sale for this item is in progress".
    The production data says otherwise: sampled across 250 unavailable
    listings, 72 percent are RESERVED, and they include items whose
    auctions last moved in 2023. RESERVED is not a waiting room, it is
    where a sold item comes to rest.

    The result was that the majority of the sold pages Google sends people
    to announced a sale in progress on something sold three years ago.
    Whichever way that copy is wrong, "sold" is wrong less often, and it
    is the honest answer for a visitor whose next move is the alternatives
    below either way.

    This is a COPY change and nothing else. `variant` still arrives
    unchanged from `resolveProductAvailability`, so the same pages render,
    with the same status codes, and a reserved listing still leaves its
    indexation untouched. The structured data also still refuses to claim
    a sale for anything that is not COMPLETED: `claimsSale` in
    `SoftSoldProduct` is deliberately not keyed off this map, because
    "SoldOut" in schema.org is a machine-readable assertion about the
    transaction rather than a sentence a shopper reads.
  */
  const claimsSold =
    variant === UNAVAILABLE_VARIANT.Sold ||
    variant === UNAVAILABLE_VARIANT.Pending;

  const headline = claimsSold
    ? t("pdp:pdp-sold-summary-headline", {
        defaultValue: "This item has been sold",
      })
    : t("pdp:pdp-sold-strip-headline-withdrawn", {
        defaultValue: "This item is no longer available",
      });

  const badgeLabel = claimsSold
    ? t("pdp:pdp-sold-strip-badge", { defaultValue: "Sold" })
    : t("pdp:pdp-unavailable-badge-withdrawn", {
        defaultValue: "No longer available",
      });

  // The same resolver that prices the JSON-LD offer, so the summary and
  // the schema print the same number, including on a zero amount, which
  // both skip.
  const askedPrice = resolveAskingPrice(product);

  const askedPriceLabel = askedPrice
    ? formatPrice(askedPrice, { locale: resolvedLocale })
    : null;

  const condition = product.quality
    ? t(`common:condition-${String(product.quality).toLowerCase()}`)
    : null;

  // Brand, then subcategory, then condition: the three facets that make
  // the alternatives below legible as alternatives. Joined with a middle
  // dot rather than a dash, because this is a list and not a sentence.
  const facets = [
    brandTitle(product),
    resolveCategoryTitle(product),
    condition,
  ].filter(Boolean);

  /*
    Spec pills: facts about the object, read from the same translated
    attributes the specifications block used. Only the ones the listing
    actually carries appear, so an item with no material and no style shows
    no pills rather than empty ones. The brand is deliberately not repeated
    here: it is already in the facet line directly above.
  */
  const specs = [
    ...attributesOfType(product, "Material"),
    ...attributesOfType(product, "Style"),
    ...attributesOfType(product, "Color"),
  ].slice(0, 6);

  return (
    <div
      data-testid="sold-item-summary"
      className={cn("flex flex-col gap-3 md:gap-4", className)}
    >
      <Heading as="h1" size="h4">
        {headline}
      </Heading>

      {thumbnails.length ? (
        <div
          data-testid="sold-item-summary-photos"
          className="flex flex-wrap gap-2"
        >
          {thumbnails.map((url, index) => (
            /*
              A button, because these photos have a second audience.

              The design review cut the full gallery, correctly: for the
              shopper this page is built around, reviving the sales pitch
              for something they cannot buy works against the one job the
              page has. But shipping partners look these listings up
              themselves to plan a pickup, and four 64px squares are not
              enough to see what you are collecting. CS had one tell them
              exactly that.

              A lightbox settles both. The page looks unchanged, the
              gallery section stays gone, and the photographs are one
              click away for anyone who needs them. It opens on the
              COMPLETE set, not just the four thumbnails, because the
              fifth photo is often the one showing the damage or the
              underside.

              `ProductImagesLightBox` is the same component the live PDP
              gallery and carousel already use, so zoom, keyboard
              navigation and the image analytics come with it.
            */
            <button
              type="button"
              key={url}
              onClick={() => setOpenImageIndex(index)}
              aria-label={t("pdp:pdp-show-detailed-image", {
                index: index + 1,
              })}
              className="relative size-16 shrink-0 overflow-hidden rounded-[0.25rem] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              {/*
                Eager, not lazy.

                These sit at the very top of the page, and nothing else on
                it is eager, so the whole hero was relying on the browser
                deciding to fetch a lazy image that is already in view.
                That is reliable in a foreground tab and not reliable
                anywhere else: a backgrounded tab, a prerender, a
                screenshot service or a slow first paint all showed the
                page with four empty squares where the item should be. A
                carrier planning a pickup reported exactly that, and this
                is the first thing on the page that tells anyone which
                object they are looking at.

                `priority` on the first one adds a preload hint so it
                starts before hydration; the other three are eager but
                unprioritised, which keeps them out of the way of the
                first card image below.
              */}
              <Image
                src={toThumbnailUrl(url)}
                alt={product.title}
                layout="fill"
                objectFit="cover"
                sizes="64px"
                {...(index === 0 ? { priority: true } : { loading: "eager" })}
              />
            </button>
          ))}

          <ProductImagesLightBox
            productId={product.id}
            images={product.images}
            imageIndex={openImageIndex ?? 0}
            isOpen={openImageIndex !== null}
            onClose={() => setOpenImageIndex(null)}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-1">
        <InfoLabel
          data-testid="sold-item-summary-badge"
          data-variant={variant}
          color="brand"
          variant="light"
          size="small"
          corners="rounded-full"
          weight="bold"
          className="self-start uppercase"
        >
          {badgeLabel}
        </InfoLabel>

        {/*
          The item's own title, as an h2 under the status h1. Two lines on
          a phone, where the longest titles in the catalogue
          (`the-club-lounge-chair-by-hans-kaufeld`) do not fit one.
        */}
        <Heading as="h2" size="h6" className="line-clamp-2">
          {product.title}
        </Heading>

        {facets.length ? (
          <Body
            as="p"
            color="muted"
            data-testid="sold-item-summary-facets"
            className="text-sm"
          >
            {facets.join(" · ")}
          </Body>
        ) : null}

        {askedPriceLabel ? (
          <Body
            as="p"
            color="muted"
            data-testid="sold-item-summary-price"
            className="text-sm"
          >
            {t("pdp:pdp-sold-strip-listed-at", {
              defaultValue: "It was listed at {{price}}",
              price: askedPriceLabel,
            })}
          </Body>
        ) : null}
      </div>

      {specs.length ? (
        <div
          data-testid="sold-item-summary-specs"
          className="flex flex-wrap gap-2"
        >
          {specs.map(spec => (
            <InfoLabel
              key={spec}
              color="brand"
              variant="light"
              size="small"
              corners="square"
            >
              {spec}
            </InfoLabel>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default SoldItemSummary;
